/**
 * Environment Variable Validation
 * Validates required environment variables at application startup
 */

interface EnvValidationError {
  variable: string;
  message: string;
  helpUrl?: string;
}

export function validateEnvironment(): { valid: boolean; errors: EnvValidationError[] } {
  const errors: EnvValidationError[] = [];

  // Required Supabase variables
  if (!import.meta.env.VITE_SUPABASE_URL) {
    errors.push({
      variable: 'VITE_SUPABASE_URL',
      message: 'Supabase URL is required. Get it from your Supabase project settings.',
      helpUrl: 'https://supabase.com/docs/guides/getting-started'
    });
  }

  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    errors.push({
      variable: 'VITE_SUPABASE_ANON_KEY',
      message: 'Supabase Anon Key is required. Get it from your Supabase project API settings.',
      helpUrl: 'https://supabase.com/docs/guides/api'
    });
  }

  // Optional but recommended OpenAI variable
  if (!import.meta.env.VITE_OPENAI_API_KEY) {
    console.warn(
      '⚠️  VITE_OPENAI_API_KEY not set. AI features (chatbot, scenario generation, feedback) will be disabled.'
    );
  }

  // Validate URL format for Supabase
  if (import.meta.env.VITE_SUPABASE_URL && !import.meta.env.VITE_SUPABASE_URL.startsWith('https://')) {
    errors.push({
      variable: 'VITE_SUPABASE_URL',
      message: 'Supabase URL must start with https://'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export function displayEnvErrors(errors: EnvValidationError[]): void {
  console.error('❌ Environment Configuration Error\n');
  console.error('The following required environment variables are missing or invalid:\n');
  
  errors.forEach((error, index) => {
    console.error(`${index + 1}. ${error.variable}`);
    console.error(`   ${error.message}`);
    if (error.helpUrl) {
      console.error(`   📚 Help: ${error.helpUrl}`);
    }
    console.error('');
  });

  console.error('To fix this:');
  console.error('1. Create a .env.local file in the project root');
  console.error('2. Add the required variables (see .env.example for reference)');
  console.error('3. Restart the development server\n');
}

/**
 * Display friendly error UI when environment is invalid
 */
export function renderEnvErrorUI(errors: EnvValidationError[]): string {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Configuration Error - Training Nudge</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          max-width: 600px;
          width: 100%;
          padding: 40px;
        }
        .icon {
          width: 64px;
          height: 64px;
          background: #fee2e2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          font-size: 32px;
        }
        h1 {
          color: #1f2937;
          font-size: 24px;
          margin-bottom: 12px;
          text-align: center;
        }
        p {
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 24px;
          text-align: center;
        }
        .error-list {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
        }
        .error-item {
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #fecaca;
        }
        .error-item:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }
        .error-var {
          font-family: 'Courier New', monospace;
          font-weight: bold;
          color: #dc2626;
          font-size: 14px;
        }
        .error-msg {
          color: #991b1b;
          margin-top: 4px;
          font-size: 14px;
        }
        .help-link {
          color: #2563eb;
          text-decoration: none;
          font-size: 13px;
          display: inline-block;
          margin-top: 4px;
        }
        .help-link:hover {
          text-decoration: underline;
        }
        .steps {
          background: #f9fafb;
          border-radius: 8px;
          padding: 20px;
        }
        .steps h2 {
          font-size: 16px;
          color: #1f2937;
          margin-bottom: 12px;
        }
        .steps ol {
          margin-left: 20px;
        }
        .steps li {
          color: #4b5563;
          margin-bottom: 8px;
          line-height: 1.5;
        }
        code {
          background: #e5e7eb;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="icon">⚠️</div>
        <h1>Configuration Required</h1>
        <p>Training Nudge needs some environment variables to be configured before it can start.</p>
        
        <div class="error-list">
          ${errors.map(error => `
            <div class="error-item">
              <div class="error-var">${error.variable}</div>
              <div class="error-msg">${error.message}</div>
              ${error.helpUrl ? `<a href="${error.helpUrl}" class="help-link" target="_blank">📚 View Documentation →</a>` : ''}
            </div>
          `).join('')}
        </div>

        <div class="steps">
          <h2>How to Fix:</h2>
          <ol>
            <li>Create a file named <code>.env.local</code> in your project root</li>
            <li>Add the required variables (see <code>.env.example</code> for reference)</li>
            <li>Restart the development server with <code>npm run dev</code></li>
          </ol>
        </div>
      </div>
    </body>
    </html>
  `;
}
